import axios from 'axios';
import moment from 'moment';

// DhanHQ API v2 base URLs
const DHAN_API_URL = 'https://api.dhan.co/v2';

// Server endpoints (credentials stay server-side)
const API_BASE = process.env.VUE_APP_API_BASE || '';

// Timeframe mapping: app timeframes → DhanHQ resolution values
// DhanHQ supports: 1, 5, 15, 25, 60 (minutes), D (day)
const TIMEFRAME_MAP = {
    '1m': '1',
    '3m': '1',     // Use 1min and aggregate
    '5m': '5',
    '10m': '5',    // Use 5min and aggregate
    '15m': '15',
    '20m': '15',   // Use 15min and aggregate
    '25m': '25',
    '30m': '15',   // Use 15min and aggregate
    '1H': '60',
    '2H': '60',    // Use 60min and aggregate
    '4H': '60',    // Use 60min and aggregate
    '1D': 'D',
    '1W': 'D',     // Use daily and aggregate
    '1M': 'D'      // Use daily and aggregate
};

// Aggregation factors for timeframes that need combining
const AGGREGATION_MAP = {
    '3m': 3,     // 3 x 1min candles
    '10m': 2,    // 2 x 5min candles
    '20m': 2,    // ~2 x 15min (approximate)
    '30m': 2,    // 2 x 15min candles
    '2H': 2,     // 2 x 60min candles
    '4H': 4      // 4 x 60min candles
};

// Supported timeframes for UI
export const DHAN_SUPPORTED_TFS = ['1m', '5m', '15m', '25m', '30m', '1H', '2H', '4H', '1D', '1W', '1M'];

export default {
    accessToken: null,
    clientId: null,
    wsConnection: null,
    ws: null,
    symbols: [],
    reconnectAttempts: 0,
    _wsOnMessage: null,
    _wsOnError: null,
    _wsSymbols: null,

    // ─── Authentication (Direct connect using env access token) ───

    /**
     * Connect to DhanHQ using server-configured access token.
     * No OAuth popup needed — just validates and stores the connection.
     */
    async connect() {
        const token = await this._getClerkToken();
        const response = await axios.post(API_BASE + '/api/dhan/connect', {}, {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        if (response.data && response.data.success) {
            this.accessToken = response.data.accessToken;
            this.clientId = response.data.clientId;
            localStorage.setItem('dhan_access_token', this.accessToken);
            localStorage.setItem('dhan_client_id', this.clientId);
            return { success: true, accessToken: this.accessToken };
        }
        throw new Error(response.data?.error || 'Failed to connect to DhanHQ');
    },

    /**
     * Restore DhanHQ session from server.
     */
    async restoreSessionFromServer() {
        try {
            const token = await this._getClerkToken();
            if (!token) return false;

            const response = await axios.get(API_BASE + '/api/dhan/session', {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data && response.data.connected && response.data.accessToken) {
                this.accessToken = response.data.accessToken;
                this.clientId = response.data.clientId;
                localStorage.setItem('dhan_access_token', this.accessToken);
                localStorage.setItem('dhan_client_id', this.clientId);
                return true;
            }
        } catch (error) {
            console.warn('[DhanHQ] Could not restore session:', error.message);
        }
        return false;
    },

    /**
     * Validate the current DhanHQ session.
     */
    async validateSession() {
        if (!this.isAuthenticated()) {
            return { valid: false, expired: false, neverConnected: true };
        }
        try {
            const token = await this._getClerkToken();
            if (!token) return { valid: false, expired: false };

            const response = await axios.get(API_BASE + '/api/dhan/session', {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data && response.data.connected && response.data.accessToken) {
                return { valid: true, expired: false };
            }
            return { valid: false, expired: true };
        } catch (error) {
            console.warn('[DhanHQ] validateSession error:', error.message);
            return { valid: false, expired: false };
        }
    },

    /**
     * Disconnect DhanHQ — clear local + server session.
     */
    async disconnectFromServer() {
        try {
            const token = await this._getClerkToken();
            if (token) {
                await axios.post(API_BASE + '/api/dhan/disconnect', {}, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            }
        } catch (error) {
            console.warn('[DhanHQ] Could not clear session on server:', error.message);
        }
        this.clearSession();
    },

    /**
     * Get the Clerk JWT token for authenticated API calls.
     */
    async _getClerkToken() {
        try {
            if (window.__vue_clerk && window.__vue_clerk.getToken) {
                return await window.__vue_clerk.getToken();
            }
            if (window.Clerk && window.Clerk.session) {
                return await window.Clerk.session.getToken();
            }
            if (window.Clerk && window.Clerk.getToken) {
                return await window.Clerk.getToken();
            }
        } catch (e) {
            console.warn('[DhanHQ] Could not get Clerk token:', e.message);
        }
        return null;
    },

    /**
     * Check if user has a valid DhanHQ session.
     */
    isAuthenticated() {
        return !!(this.accessToken || localStorage.getItem('dhan_access_token'));
    },

    /**
     * Load saved session from localStorage.
     */
    loadSavedSession() {
        this.accessToken = localStorage.getItem('dhan_access_token');
        this.clientId = localStorage.getItem('dhan_client_id');
        return this.accessToken;
    },

    /**
     * Clear session and remove stored credentials.
     */
    clearSession() {
        this.accessToken = null;
        this.clientId = null;
        localStorage.removeItem('dhan_access_token');
        localStorage.removeItem('dhan_client_id');
        this.closeWebSocket();
    },

    /**
     * Fetch user profile.
     */
    async getProfile() {
        try {
            const token = await this._getClerkToken();
            const response = await axios.get(API_BASE + '/api/dhan/profile', {
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            });
            return response.data;
        } catch (error) {
            console.error('[DhanHQ] profile error:', error);
            throw error;
        }
    },

    // ─── Symbol Search ───

    /**
     * Search symbols via DhanHQ API through backend proxy.
     */
    async searchSymbols(query, limit = 30) {
        if (!query || query.length < 1) return [];

        try {
            const token = await this._getClerkToken();
            if (!token) return [];

            const response = await axios.get(API_BASE + '/api/dhan/search', {
                params: { query: query.toUpperCase(), limit },
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data && response.data.success) {
                this.symbols = response.data.symbols || [];
                return this.symbols;
            }
            return [];
        } catch (error) {
            console.error('[DhanHQ] search error:', error);
            return [];
        }
    },

    /**
     * Load symbol master.
     */
    async loadSymbolMaster(exchange = 'NSE_EQ') {
        return this.searchSymbols('A', 100);
    },

    // ─── Historical Data ───

    /**
     * Load historical candle data through backend proxy.
     * Returns array of [timestamp, open, high, low, close, volume].
     */
    async loadHistoricalData(symbol, timeframe, _retryCount, endTime) {
        const retryCount = _retryCount || 0;

        if (!this.isAuthenticated()) {
            throw new Error('User not authenticated with DhanHQ');
        }

        if (!symbol || typeof symbol !== 'string' || symbol.trim() === '') {
            throw new Error('Symbol is required but not provided');
        }

        if (!timeframe || !TIMEFRAME_MAP[timeframe]) {
            throw new Error(`Invalid timeframe: ${timeframe}. Supported: ${Object.keys(TIMEFRAME_MAP).join(', ')}`);
        }

        const resolution = TIMEFRAME_MAP[timeframe];
        const toDate = (Number.isFinite(endTime) ? moment(endTime) : moment()).format('YYYY-MM-DD');
        const fromDate = this._calculateFromDate(timeframe, endTime);

        try {
            const token = await this._getClerkToken();
            if (!token) throw new Error('Not authenticated');

            const response = await axios.get(API_BASE + '/api/dhan/history', {
                params: {
                    symbol: symbol.trim(),
                    resolution,
                    from_date: fromDate,
                    to_date: toDate,
                    ...this._resolveInstrumentMetadata(symbol)
                },
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data && response.data.success && response.data.data) {
                let candles = response.data.data.candles || response.data.data || [];

                // Convert to app format: [timestamp, open, high, low, close, volume]
                if (candles.length > 0 && candles[0].length !== 6) {
                    candles = candles.map(c => {
                        const ts = moment(c.start_Time || c.timestamp || c[0]).valueOf();
                        return [ts, c.open || c[1], c.high || c[2], c.low || c[3], c.close || c[4], c.volume || c[5] || 0];
                    });
                }

                // Sort by timestamp ascending
                candles.sort((a, b) => a[0] - b[0]);

                // Aggregate if needed
                const aggFactor = AGGREGATION_MAP[timeframe];
                if (aggFactor && aggFactor > 1) {
                    candles = this._aggregateCandles(candles, aggFactor);
                }

                return candles;
            } else {
                throw new Error(response.data?.error || 'Failed to load historical data');
            }
        } catch (error) {
            const status = error.response && error.response.status;

            if (status === 401) {
                this.clearSession();
                throw new Error('DhanHQ session expired. Please reconnect.');
            }

            if (retryCount < 2 && (status === 429 || (status >= 500 && status < 600))) {
                const backoff = (retryCount + 1) * 2000;
                await new Promise(r => setTimeout(r, backoff));
                return this.loadHistoricalData(symbol, timeframe, retryCount + 1, endTime);
            }

            throw error;
        }
    },

    /**
     * Calculate from date based on timeframe.
     */
    _calculateFromDate(timeframe, endTime) {
        const now = Number.isFinite(endTime) ? moment(endTime) : moment();
        const OPTIMAL_DAYS = {
            '1m': 5, '3m': 10, '5m': 10, '10m': 15, '15m': 25,
            '20m': 45, '25m': 45, '30m': 45, '1H': 60, '2H': 75,
            '4H': 90, '1D': 365, '1W': 365 * 3, '1M': 365 * 10
        };
        const safeDays = OPTIMAL_DAYS[timeframe] || 30;
        return now.clone().subtract(safeDays, 'days').format('YYYY-MM-DD');
    },

    /**
     * Aggregate candles by factor.
     */
    _aggregateCandles(candles, factor) {
        if (!candles || candles.length === 0 || factor <= 1) return candles;

        const sourceInterval = candles.length > 1 ? (candles[1][0] - candles[0][0]) : 60000;
        const targetInterval = sourceInterval * factor;
        const aggregated = [];
        let currentGroup = [];
        let currentBoundary = 0;

        for (const candle of candles) {
            const boundary = candle[0] - (candle[0] % targetInterval);
            if (boundary !== currentBoundary && currentGroup.length > 0) {
                aggregated.push(this._combineGroup(currentGroup));
                currentGroup = [];
            }
            currentBoundary = boundary;
            currentGroup.push(candle);
        }

        if (currentGroup.length > 0) {
            aggregated.push(this._combineGroup(currentGroup));
        }

        return aggregated;
    },

    /**
     * Combine a group of candles into a single OHLCV candle.
     */
    _combineGroup(group) {
        if (group.length === 0) return null;
        if (group.length === 1) return group[0];

        const first = group[0];
        const last = group[group.length - 1];
        let high = first[2];
        let low = first[3];
        let volume = 0;

        for (const c of group) {
            high = Math.max(high, c[2]);
            low = Math.min(low, c[3]);
            volume += c[5] || 0;
        }

        return [first[0], first[1], high, low, last[4], volume];
    },

    // ─── WebSocket (Real-time Data) ───

    /**
     * Connect to DhanHQ WebSocket for real-time market data.
     */
    async connectWebSocket(symbols, onMessage) {
        if (!this.isAuthenticated()) {
            console.error('[DhanHQ] Cannot connect WebSocket: not authenticated');
            return null;
        }

        this.closeWebSocket();

        try {
            const token = await this._getClerkToken();
            if (!token) return null;

            const response = await axios.get(API_BASE + '/api/dhan/ws-url', {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!response.data || !response.data.success || !response.data.wsUrl) {
                console.error('[DhanHQ] Failed to get WebSocket URL');
                return null;
            }

            const wsUrl = response.data.wsUrl;
            this._wsSymbols = symbols;
            this._wsOnMessage = onMessage;

            this.ws = new WebSocket(wsUrl);
            this.ws.binaryType = 'arraybuffer';

            this.ws.onopen = () => {
                this.reconnectAttempts = 0;
                // Subscribe to symbols
                if (symbols && Array.isArray(symbols)) {
                    this._subscribeSymbols(symbols);
                }
            };

            this.ws.onmessage = (event) => {
                try {
                    let data;
                    if (typeof event.data === 'string') {
                        data = JSON.parse(event.data);
                    } else if (event.data instanceof ArrayBuffer) {
                        data = this._parseBinaryMessage(event.data);
                    }

                    if (data && onMessage) {
                        const normalized = this._normalizeWsMessage(data);
                        if (normalized) onMessage(normalized);
                    }
                } catch (error) {
                    console.error('[DhanHQ] Error processing WS message:', error);
                }
            };

            this.ws.onerror = (error) => {
                console.error('[DhanHQ] WebSocket error:', error);
                if (this._wsOnError) this._wsOnError(error);
            };

            this.ws.onclose = (event) => {
                if (event.code === 1000 || !this.isAuthenticated()) return;

                if (this.reconnectAttempts < 5) {
                    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
                    this.reconnectAttempts++;
                    setTimeout(() => {
                        this.connectWebSocket(this._wsSymbols, this._wsOnMessage);
                    }, delay);
                }
            };

            return this.ws;
        } catch (error) {
            console.error('[DhanHQ] WebSocket connection error:', error);
            return null;
        }
    },

    /**
     * Subscribe to symbols on the WebSocket.
     */
    _subscribeSymbols(symbols) {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;

        const normalizedSymbols = symbols.map(s => this._resolveInstrumentMetadata(s));
        for (let i = 0; i < normalizedSymbols.length; i += 100) {
            const batch = normalizedSymbols.slice(i, i + 100);
            const subscribeMsg = {
                RequestCode: 15,
                InstrumentCount: batch.length,
                InstrumentList: batch.map(s => ({
                    ExchangeSegment: s.exchange_segment,
                    SecurityId: String(s.security_id)
                }))
            };

            try {
                this.ws.send(JSON.stringify(subscribeMsg));
            } catch (error) {
                console.error('[DhanHQ] Failed to send subscription:', error);
            }
        }
    },

    subscribeToSymbol(symbol) {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
        this._subscribeSymbols([symbol]);
    },

    unsubscribeFromSymbol(symbol) {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;

        const instrument = this._resolveInstrumentMetadata(symbol);
        const unsubscribeMsg = {
            RequestCode: 16,
            InstrumentCount: 1,
            InstrumentList: [{
                ExchangeSegment: instrument.exchange_segment,
                SecurityId: String(instrument.security_id)
            }]
        };

        try {
            this.ws.send(JSON.stringify(unsubscribeMsg));
        } catch (error) {
            console.error('[DhanHQ] Failed to unsubscribe:', error);
        }
    },

    closeWebSocket() {
        if (this.ws) {
            this.ws.onclose = null;
            this.ws.close(1000, 'Manual close');
            this.ws = null;
        }
        this.reconnectAttempts = 0;
        this._wsSymbols = null;
    },

    destroyWs() {
        this.closeWebSocket();
    },

    createWebSocket(onMessage, onError, symbols) {
        this._wsOnError = onError;
        return this.connectWebSocket(symbols || null, onMessage);
    },

    // ─── Utility ───

    _resolveInstrumentMetadata(symbol) {
        if (symbol && typeof symbol === 'object') {
            return {
                security_id: symbol.security_id || symbol.securityId || this._getSecurityId(symbol.symbol || symbol.trading_symbol || ''),
                exchange_segment: symbol.exchange_segment || symbol.exchangeSegment || this._getExchangeSegment(symbol.symbol || symbol.exchange || ''),
                instrument: symbol.instrument || 'EQUITY'
            };
        }

        const match = this.symbols.find(item => {
            return item.symbol === symbol ||
                item.trading_symbol === symbol ||
                item.security_id === symbol ||
                item.securityId === symbol;
        });
        if (!match) {
            return {
                security_id: this._getSecurityId(symbol),
                exchange_segment: this._getExchangeSegment(symbol),
                instrument: 'EQUITY'
            };
        }
        return {
            security_id: match.security_id || match.securityId || this._getSecurityId(match.symbol || symbol),
            exchange_segment: match.exchange_segment || match.exchangeSegment || this._getExchangeSegment(match.symbol || symbol),
            instrument: match.instrument || 'EQUITY'
        };
    },

    _getExchangeSegment(symbol) {
        if (!symbol) return 'NSE_EQ';
        if (symbol.includes(':')) {
            const exch = symbol.split(':')[0].toUpperCase();
            const map = { 'NSE': 'NSE_EQ', 'BSE': 'BSE_EQ', 'NFO': 'NSE_FNO', 'MCX': 'MCX_COMM' };
            return map[exch] || 'NSE_EQ';
        }
        return 'NSE_EQ';
    },

    _getSecurityId(symbol) {
        if (!symbol) return '';
        if (symbol.includes(':')) return symbol.split(':')[1];
        return symbol;
    },

    _parseBinaryMessage(buffer) {
        try {
            const text = new TextDecoder().decode(buffer);
            if (text.startsWith('{') || text.startsWith('[')) {
                return JSON.parse(text);
            }
        } catch (e) {
            // Binary packets are parsed below.
        }

        if (!buffer || buffer.byteLength < 8) return null;
        const view = new DataView(buffer);
        const code = view.getUint8(0);
        const messageLength = view.getInt16(1, true);
        const exchangeSegmentCode = view.getUint8(3);
        const securityId = String(view.getInt32(4, true));
        const base = {
            code,
            messageLength,
            exchangeSegmentCode,
            security_id: securityId,
            SecurityId: securityId,
            type: 'dhan_binary'
        };

        if (code === 2 && buffer.byteLength >= 16) {
            return {
                ...base,
                packetType: 'ticker',
                ltp: view.getFloat32(8, true),
                LTP: view.getFloat32(8, true),
                LTT: view.getInt32(12, true)
            };
        }

        if (code === 4 && buffer.byteLength >= 50) {
            return {
                ...base,
                packetType: 'quote',
                ltp: view.getFloat32(8, true),
                lastTradedQuantity: view.getInt16(12, true),
                LTT: view.getInt32(14, true),
                averageTradePrice: view.getFloat32(18, true),
                volume: view.getInt32(22, true),
                totalSellQuantity: view.getInt32(26, true),
                totalBuyQuantity: view.getInt32(30, true),
                open: view.getFloat32(34, true),
                close: view.getFloat32(38, true),
                high: view.getFloat32(42, true),
                low: view.getFloat32(46, true)
            };
        }

        if (code === 8 && buffer.byteLength >= 62) {
            return {
                ...base,
                packetType: 'full',
                ltp: view.getFloat32(8, true),
                lastTradedQuantity: view.getInt16(12, true),
                LTT: view.getInt32(14, true),
                averageTradePrice: view.getFloat32(18, true),
                volume: view.getInt32(22, true),
                totalSellQuantity: view.getInt32(26, true),
                totalBuyQuantity: view.getInt32(30, true),
                openInterest: view.getInt32(34, true),
                highOpenInterest: view.getInt32(38, true),
                lowOpenInterest: view.getInt32(42, true),
                open: view.getFloat32(46, true),
                close: view.getFloat32(50, true),
                high: view.getFloat32(54, true),
                low: view.getFloat32(58, true)
            };
        }

        if (code === 6 && buffer.byteLength >= 16) {
            return {
                ...base,
                packetType: 'previous_close',
                close: view.getFloat32(8, true),
                openInterest: view.getInt32(12, true)
            };
        }

        if (code === 50 && buffer.byteLength >= 10) {
            return {
                ...base,
                packetType: 'disconnect',
                disconnectCode: view.getInt16(8, true)
            };
        }

        return base;
    },

    _normalizeWsMessage(data) {
        if (!data) return null;

        // DhanHQ tick format
        if (data.LTP || data.ltp) {
            return {
                symbol: data.security_id || data.SecurityId || '',
                ltp: Number(data.LTP || data.ltp) || 0,
                timestamp: data.LTT ? Number(data.LTT) * 1000 : Date.now(),
                volume: Number(data.volume || data.Volume) || 0,
                close_price: Number(data.close || data.Close) || 0,
                type: 'dhan_tick'
            };
        }

        return data;
    },

    async checkAuthStatus() {
        try {
            const token = await this._getClerkToken();
            if (!token) return { success: false };

            const response = await axios.get(API_BASE + '/api/dhan/session', {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data && response.data.connected && response.data.accessToken) {
                this.accessToken = response.data.accessToken;
                this.clientId = response.data.clientId;
                localStorage.setItem('dhan_access_token', this.accessToken);
                localStorage.setItem('dhan_client_id', this.clientId);
                return { success: true, accessToken: this.accessToken };
            }
            return { success: false };
        } catch (error) {
            return { success: false };
        }
    }
};
